import { Box, Tooltip } from '@mui/material';
import React from 'react';
import { match } from 'ts-pattern';

export interface FilePathTruncateProps {
  /**
   * The file path to display.
   */
  filePath: string | string[];
  /**
   * The maximum number of characters to display.
   */
  maxCharacters?: number;
  /**
   * Disables the hint when the file path is truncated.
   */
  hint?: 'never' | 'always' | 'auto';
}

export const FilePathTruncate: React.FC<FilePathTruncateProps> = ({
  hint = 'auto',
  filePath,
  maxCharacters = CHARACTER_MAX_DEFAULT,
}) => {
  const filePathParts = getFilePathParts(filePath);

  const fullValue = React.useMemo(() => {
    return filePathParts.join('/');
  }, [filePathParts]);

  const truncate = React.useMemo(() => {
    return shouldTruncate(filePathParts, maxCharacters);
  }, [filePathParts, maxCharacters]);

  const showHint = React.useMemo(() => {
    return match({ hint, truncate })
      .with({ hint: 'never' }, () => false)
      .with({ hint: 'always' }, () => true)
      .with({ hint: 'auto' }, ({ truncate }) => truncate)
      .exhaustive();
  }, [hint, truncate]);

  const value = React.useMemo(() => {
    if (truncate) {
      return truncateFilePath(filePathParts, maxCharacters);
    }
    return fullValue;
  }, [truncate, fullValue, filePathParts, maxCharacters]);

  return (
    <Tooltip title={showHint ? fullValue : undefined}>
      <Box component='span' sx={{ cursor: 'pointer' }}>
        {value}
      </Box>
    </Tooltip>
  );
};

export default FilePathTruncate;

const CHARACTER_MAX_DEFAULT = 60;

/*
|------------------
| Utils
|------------------
*/

function shouldTruncate(
  filePathParts: string[],
  maxCharacters: number
): boolean {
  return filePathParts.join('/').length > maxCharacters;
}

function getFilePathParts(filePath: string | string[]): string[] {
  if (typeof filePath === 'string') {
    return filePath.split('/');
  }
  return filePath;
}

function truncateFilePath(
  filePathParts: string[],
  maxCharacters: number
): string {
  const firstPart = filePathParts[0];
  const lastPart = filePathParts[filePathParts.length - 1];

  const value = `${firstPart}/.../${lastPart}`;

  if (value.length > maxCharacters) {
    if (firstPart.length > lastPart.length) {
      return `${firstPart.slice(0, 5)}.../${lastPart}`;
    }
    return `${firstPart}/.../...${lastPart.slice(lastPart.length - 5, lastPart.length)}`;
  }
  return value;
}
